import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafTextareaLightComponent } from '../../../../components/enfaf-textarea-light/enfaf-textarea-light.component';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';

export interface ClassRatingDialogData {
  courseClassId: string;
}

export interface ClassRatingDialogResult {
  action: 'submitted' | 'skipped';
  rating?: number;
}

@Component({
  selector: 'app-class-rating-modal',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, EnfafIconComponent, EnfafTextareaLightComponent, EnfafButtonComponent],
  templateUrl: './class-rating-modal.component.html',
  styleUrl: './class-rating-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClassRatingModalComponent {
  private api = inject(ApiService);
  private toast = inject(ToastService);
  private dialogRef = inject(MatDialogRef<ClassRatingModalComponent>);
  private data: ClassRatingDialogData = inject(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  rating = signal(0);
  comment = signal('');
  isSubmitting = signal(false);
  hoveredStar = signal(0);
  commentControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(2000)] });

  stars = [1, 2, 3, 4, 5];

  constructor() {
    this.commentControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.onCommentChange(value ?? ''));
  }

  onStarClick(value: number): void {
    this.rating.set(value);
  }

  onStarHover(value: number): void {
    this.hoveredStar.set(value);
  }

  onStarLeave(): void {
    this.hoveredStar.set(0);
  }

  onCommentChange(value: string): void {
    this.comment.set(value);
  }

  async onSubmit(): Promise<void> {
    if (this.rating() < 1) return;
    this.isSubmitting.set(true);

    try {
      const commentValue = this.comment().trim() || undefined;
      await firstValueFrom(this.api.submitClassRating(this.data.courseClassId, this.rating(), commentValue));
      this.toast.presentSuccessToast('¡Gracias por tu valoración!');
      this.dialogRef.close({ action: 'submitted', rating: this.rating() } as ClassRatingDialogResult);
    } catch {
      this.toast.presentErrorToast('Error al enviar la valoración');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  onSkip(): void {
    this.dialogRef.close({ action: 'skipped' } as ClassRatingDialogResult);
  }
}
